import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { LOCALE_STORAGE_KEY } from "./ui/shell/boot-script";

import ptBRCommon from "./locales/pt-BR/common.json";
import ptBRNav from "./locales/pt-BR/nav.json";
import ptBRUi from "./locales/pt-BR/ui.json";
import ptBRAuth from "./locales/pt-BR/auth.json";
import ptBROverview from "./locales/pt-BR/overview.json";
import ptBRAccounts from "./locales/pt-BR/accounts.json";
import ptBRConvoys from "./locales/pt-BR/convoys.json";
import ptBRDrives from "./locales/pt-BR/drives.json";
import ptBRSystem from "./locales/pt-BR/system.json";
import ptBROperators from "./locales/pt-BR/operators.json";
import ptBRAudit from "./locales/pt-BR/audit.json";
import ptBRProfile from "./locales/pt-BR/profile.json";
import enUSCommon from "./locales/en-US/common.json";
import enUSNav from "./locales/en-US/nav.json";
import enUSUi from "./locales/en-US/ui.json";
import enUSAuth from "./locales/en-US/auth.json";
import enUSOverview from "./locales/en-US/overview.json";
import enUSAccounts from "./locales/en-US/accounts.json";
import enUSConvoys from "./locales/en-US/convoys.json";
import enUSDrives from "./locales/en-US/drives.json";
import enUSSystem from "./locales/en-US/system.json";
import enUSOperators from "./locales/en-US/operators.json";
import enUSAudit from "./locales/en-US/audit.json";
import enUSProfile from "./locales/en-US/profile.json";

export const SUPPORTED_LOCALES = ["pt-BR", "en-US"] as const;
export { LOCALE_STORAGE_KEY };
export type AdminLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AdminLocale = "pt-BR";

export const translations = {
  "pt-BR": {
    common: ptBRCommon,
    nav: ptBRNav,
    ui: ptBRUi,
    auth: ptBRAuth,
    overview: ptBROverview,
    accounts: ptBRAccounts,
    convoys: ptBRConvoys,
    drives: ptBRDrives,
    system: ptBRSystem,
    operators: ptBROperators,
    audit: ptBRAudit,
    profile: ptBRProfile,
  },
  "en-US": {
    common: enUSCommon,
    nav: enUSNav,
    ui: enUSUi,
    auth: enUSAuth,
    overview: enUSOverview,
    accounts: enUSAccounts,
    convoys: enUSConvoys,
    drives: enUSDrives,
    system: enUSSystem,
    operators: enUSOperators,
    audit: enUSAudit,
    profile: enUSProfile,
  },
};

export function isAdminLocale(value: unknown): value is AdminLocale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function detectLocale(): AdminLocale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isAdminLocale(stored)) return stored;
  } catch {}
  const preferred = typeof navigator === "undefined" ? [] : navigator.languages ?? [navigator.language];
  for (const language of preferred) {
    if (language.toLowerCase().startsWith("en")) return "en-US";
    if (language.toLowerCase().startsWith("pt")) return "pt-BR";
  }
  return DEFAULT_LOCALE;
}

type CardinalRule = { select: (count: number) => string; resolvedOptions: () => { pluralCategories: string[] } };

const PORTUGUESE_ONLY_ONE_IS_SINGULAR: CardinalRule = {
  select: (count) => (count === 1 ? "one" : "other"),
  resolvedOptions: () => ({ pluralCategories: ["one", "other"] }),
};

function applyPortugueseCardinalRule(instance: typeof i18n) {
  const resolver = instance.services.pluralResolver as {
    getRule: (code: string, options?: { ordinal?: boolean }) => unknown;
  };
  const cldrRule = resolver.getRule.bind(resolver);
  resolver.getRule = (code, options = {}) =>
    !options.ordinal && String(code).toLowerCase().startsWith("pt")
      ? PORTUGUESE_ONLY_ONE_IS_SINGULAR
      : cldrRule(code, options);
}

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources: {
      "pt-BR": { translation: translations["pt-BR"] },
      "en-US": { translation: translations["en-US"] },
    },
    lng: detectLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    interpolation: { escapeValue: false },
    initAsync: false,
  });
  applyPortugueseCardinalRule(i18n);
}

export default i18n;
